import { Fragment, useMemo, useState } from 'react';
import { XCircle, AlertTriangle, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import {
  DASHBOARD_WIDGETS,
  DASHBOARD_WIDGETS_PREFERENCE_KEY,
  type DashboardWidgetKey,
  type DashboardWidgetLayout,
} from '@efacture/core/sector';

import { useTranslation } from '@/lib/i18n';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import {
  useClearOwnUiPreference,
  useSetOwnUiPreference,
} from '@/api/queries/ui-preferences';
import { DashboardWidgetPicker } from './DashboardWidgetPicker';
import { useDashboardWidgets } from './useDashboardWidgets';
import {
  useDashboardClientStats,
  useDashboardArticleStats,
  useDashboardLowStock,
  useDashboardInvoiceStats,
  useDashboardSummary,
} from '@/api/queries/dashboard';
import {
  DASHBOARD_WIDGET_COMPONENTS,
  SOURCE_PERMISSION,
  isSummaryBacked,
  type DashboardSource,
  type DashboardWidgetProps,
} from './widgets';

// ─────────────────────────────────────────────────────────────
// Dashboard Page Component
// ─────────────────────────────────────────────────────────────

/**
 * The dashboard composed for the roles its reader holds.
 *
 * I-RP1 — the role only SELECTS. Each widget keeps the permission its
 * data demands, checked here, so a role can never surface a figure the
 * permission layer withholds.
 *
 * I-RP2 — a user with no role, an unknown code, or a role whose station
 * no endpoint serves resolves to the whole catalogue in its original
 * order, which is the screen exactly as it stood before roles composed
 * it.
 */
export function DashboardPage() {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();

  // The role's widgets, narrowed by plan, permission, the company default
  // and the reader's own choice. Nothing is fetched until it is known.
  const { keys: widgetKeys, options, hasOwnChoice } = useDashboardWidgets();

  const [customizing, setCustomizing] = useState(false);
  const saveOwn = useSetOwnUiPreference<DashboardWidgetKey[]>(DASHBOARD_WIDGETS_PREFERENCE_KEY);
  const clearOwn = useClearOwnUiPreference(DASHBOARD_WIDGETS_PREFERENCE_KEY);

  /** Would this source's endpoint answer this user at all? */
  const allowsSource = (source: DashboardSource) => {
    const permission = SOURCE_PERMISSION[source];
    return permission === null || hasPermission(permission);
  };

  // A source is fetched only when a visible widget reads it AND its
  // endpoint would answer. Withholding the request is what keeps an
  // unpermitted figure absent rather than showing up as a zero.
  const needsSource = (source: DashboardSource) =>
    allowsSource(source) &&
    widgetKeys.some((key) =>
      DASHBOARD_WIDGET_COMPONENTS[key].needs.includes(source)
    );

  const {
    data: clientStats,
    isLoading: clientStatsLoading,
    isError: clientStatsError,
  } = useDashboardClientStats({ enabled: needsSource('clients') });
  const {
    data: articleStats,
    isLoading: articleStatsLoading,
    isError: articleStatsError,
  } = useDashboardArticleStats({ enabled: needsSource('articles') });
  const {
    data: lowStockArticles,
    isLoading: lowStockLoading,
    isError: lowStockError,
  } = useDashboardLowStock({ enabled: needsSource('lowStock') });
  const {
    data: invoiceStats,
    isLoading: invoiceStatsLoading,
    isError: invoiceStatsError,
  } = useDashboardInvoiceStats({ enabled: needsSource('invoices') });

  // One request for every block `/dashboard/summary` serves. The list is
  // sent whole: the server checks each key against its own permission
  // and answers with the subset it may serve, so a key this reader turns
  // out not to hold comes back absent rather than as an error.
  const summaryKeys = useMemo(
    () => widgetKeys.filter(isSummaryBacked),
    [widgetKeys]
  );
  const { data: summary, isError: summaryError } =
    useDashboardSummary(summaryKeys);

  // A block whose computation THREW comes back named in `failed`, not
  // refused. Without this notice it is indistinguishable from a block the
  // reader may not see — the same absence, two opposite meanings.
  const failedBlocks = summary?.failed ?? [];

  const isError =
    clientStatsError ||
    articleStatsError ||
    lowStockError ||
    invoiceStatsError ||
    summaryError;

  // Data a permission withholds is withheld from the widgets too, not
  // merely left unfetched: a disabled query still serves whatever the
  // cache already holds, and an open widget reading a permissioned
  // source — the low-stock half of the alerts — would surface it.
  //
  // A disabled query also reports `isLoading` forever; a block whose
  // source was never asked for is not waiting for anything.
  const widgetProps: DashboardWidgetProps = {
    clientStats: allowsSource('clients') ? clientStats : undefined,
    clientStatsLoading: clientStatsLoading && needsSource('clients'),
    articleStats: allowsSource('articles') ? articleStats : undefined,
    articleStatsLoading: articleStatsLoading && needsSource('articles'),
    lowStockArticles: allowsSource('lowStock') ? lowStockArticles : undefined,
    lowStockLoading: lowStockLoading && needsSource('lowStock'),
    invoiceStats: allowsSource('invoices') ? invoiceStats : undefined,
    invoiceStatsLoading: invoiceStatsLoading && needsSource('invoices'),
    summaryBlocks: summary?.blocks,
  };

  // A summary-backed block is drawn only once its figure is in hand. An
  // absent block — refused, module off, or nothing to stand on — draws
  // no card at all, and neither does one still in flight: an empty
  // skeleton would promise a figure the server may never send.
  const renderedKeys = visibleWidgetKeys(
    widgetKeys,
    summary?.blocks as Record<string, unknown> | undefined
  );

  return (
    <div className="space-y-8">
      {/* ─────────────────────────────────────────────────────── */}
      {/* Page Header */}
      {/* ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('nav.dashboard')}</h1>
          <p className="mt-1 text-slate-500">{t('dashboard.welcome')}</p>
        </div>
        {!customizing && options.length > 0 && (
          <Button type="button" variant="outline" onClick={() => setCustomizing(true)}>
            <SlidersHorizontal className="h-4 w-4" />
            {t('dashboard.customize.button')}
          </Button>
        )}
      </div>

      {customizing && (
        <DashboardWidgetPicker
          options={options}
          selected={widgetKeys}
          pending={saveOwn.isPending || clearOwn.isPending}
          saveLabel={t('dashboard.customize.save')}
          onSave={(keys) =>
            saveOwn.mutate(keys, {
              onSuccess: () => {
                toast.success(t('dashboard.customize.saved'));
                setCustomizing(false);
              },
              onError: () => toast.error(t('dashboard.customize.saveFailed')),
            })
          }
          secondary={[
            { label: t('dashboard.customize.cancel'), onClick: () => setCustomizing(false) },
            {
              label: t('dashboard.customize.reset'),
              disabled: !hasOwnChoice,
              onClick: () =>
                clearOwn.mutate(undefined, {
                  onSuccess: () => {
                    toast.success(t('dashboard.customize.resetDone'));
                    setCustomizing(false);
                  },
                  onError: () => toast.error(t('dashboard.customize.saveFailed')),
                }),
            },
          ]}
        />
      )}

      {/* ─────────────────────────────────────────────────────── */}
      {/* Error Banner */}
      {/* ─────────────────────────────────────────────────────── */}
      {isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-center gap-3">
          <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800">{t('dashboard.error.loadFailed')}</p>
        </div>
      )}

      {failedBlocks.length > 0 && (
        <div
          role="status"
          className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3"
        >
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            {t('dashboard.error.blocksFailed')}
          </p>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────── */}
      {/* Widgets, in the order the roles asked for them */}
      {/* ─────────────────────────────────────────────────────── */}
      {chunkWidgets(renderedKeys).map((chunk, index) => {
        const rendered = chunk.keys.map((key) => {
          const { Component } = DASHBOARD_WIDGET_COMPONENTS[key];
          return <Component key={key} {...widgetProps} />;
        });

        if (chunk.layout === 'kpi') {
          return (
            <div
              key={`kpi-${index}`}
              className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
            >
              {rendered}
            </div>
          );
        }
        if (chunk.layout === 'metric') {
          // Three columns, deliberately not the four of the KPI row: the
          // two rows are read as two different things.
          return (
            <div
              key={`metric-${index}`}
              className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3"
            >
              {rendered}
            </div>
          );
        }
        if (chunk.layout === 'half') {
          return (
            <div key={`half-${index}`} className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {rendered}
            </div>
          );
        }
        return <Fragment key={`panel-${index}`}>{rendered}</Fragment>;
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/**
 * The keys that actually get drawn.
 *
 * ★ THE FIRST OF THE TWO LAYERS that keep an unserved block off the
 * screen. This one decides whether a component is MOUNTED at all, which
 * is what keeps a withheld block out of the layout — a card filtered here
 * cannot leave an empty column behind in a grid row. The second layer is
 * the guard inside each component, which answers for a component rendered
 * directly. Each layer is pinned by its own test; either one alone would
 * hide the block, and neither alone is enough to rely on.
 *
 * A block still in flight is treated as absent: an empty skeleton would
 * promise a figure the server may never send.
 */
export function visibleWidgetKeys(
  widgetKeys: readonly DashboardWidgetKey[],
  blocks: Record<string, unknown> | undefined
): DashboardWidgetKey[] {
  return widgetKeys.filter(
    (key) => !isSummaryBacked(key) || blocks?.[key] !== undefined
  );
}

export interface WidgetChunk {
  layout: DashboardWidgetLayout;
  keys: DashboardWidgetKey[];
}

/**
 * Groups the widgets into the rows the page draws.
 *
 * Consecutive KPI cards share one responsive grid row and consecutive
 * `half` widgets pair two-per-row, as they did when the page held two
 * hard-coded grids. A `panel` stands on its own, and so does a lone `kpi`
 * or `half`: one card in a four- or two-column grid would sit at a
 * fraction of the width beside empty columns.
 */
export function chunkWidgets(keys: readonly DashboardWidgetKey[]): WidgetChunk[] {
  const chunks: WidgetChunk[] = [];
  for (const key of keys) {
    const { layout } = DASHBOARD_WIDGETS[key];
    const last = chunks[chunks.length - 1];
    if (last !== undefined && last.layout === layout && layout !== 'panel') {
      last.keys.push(key);
    } else {
      chunks.push({ layout, keys: [key] });
    }
  }
  return chunks.map((chunk) =>
    chunk.layout !== 'panel' && chunk.keys.length === 1
      ? { ...chunk, layout: 'panel' as const }
      : chunk
  );
}

export default DashboardPage;
